import Model, { ID, Integer, JsonArray, JsonObject } from 'src/models/model'
import ReportChart from 'src/models/report/ReportChart'
import ReportHtml from 'src/models/report/ReportHtml'
import ReportStatus from 'src/models/report/ReportStatus'
import ReportTable from 'src/models/report/ReportTable'
import ReportView from 'src/models/report/ReportView'
import Widget from 'src/models/report/Widget'
import { getOptionsWithSeparators } from 'src/modules/admin/report/ReportUtils'

export const CELL_VALUE_OUTPUT_OPTIONS = {
  TRUE: 'True',
  FALSE: 'False',
  EMPTY: 'Empty'
}

export const COMPONENT_TYPES = {
  MONEY_CENTS: 'money-cents',
  MONEY_CENTS_E2: 'money-cents-e2'
}

export const ELEMENT_ACTIONS = {
  MIGRATE_ELEMENT: 'migrate_element'
}

export const MIGRATABLE_COMPONENT_NAMES = ['AttendanceWidget', 'BoxOfficeSummaryTable', 'RevenueWidget', 'PageViewWidget',
  'SeriesAggregateChart', 'SeriesDataTable', 'SeriesAggregateTable', 'SeriesAreaChart',
  'SeriesLineChart', 'SeriesAggregatePieChart', 'TicketsSoldByMovieChart', 'TicketsSoldByShowingChart',
  'TransferSummaryTable', 'MembershipWidget', 'PaymentDetailTable', 'OrderItemDetailTable',
  'MembershipSales', 'TicketAttendanceReconciliation', 'TicketSalesReconciliation', 'MiscellaneousItems',
  'DiscountsUsed', 'PaymentsTable', 'ConcessionPerformance', 'BoxOfficePerformance',
  'VisitReviewTable', 'VisitReviewWidget', 'CashNotes', 'TimeClockHoursTable', 'TimeClockSessionsTable',
  'VouchersIssued', 'VouchersUsed', 'DonationsTable',
  'InventoryLevelsByLocationTable', 'InventorySalesByLocationTable', 'InventoryReceiptsByLocationTable',
  'InventoryMovementsByLocationTable', 'InventoryRecipeAvailabilityTable', 'DepositReconciliationTable',
  'SalesTable', 'ItemSales', 'SummaryReconciliationLegacy', 'InventoryWastageTableLegacy',
  'InventoryVarianceTableLegacy', 'InventoryVarianceTable', 'RegionSeriesAggregateChart', 'RegionSeriesLineChart', 'TicketSales'
]

export const SOURCE_CENT_COMPONENT_TYPES = [
  COMPONENT_TYPES.MONEY_CENTS,
  COMPONENT_TYPES.MONEY_CENTS_E2
  // There are many other component types and
  // we're only listing ones here that we need to reference
  // The global list is defined on the backend
]

export const NUMERIC_COMPONENT_TYPES = [
  'money-cents',
  'money-cents-e2',
  'integer-value',
  'decimal-1',
  'decimal-e1',
  'decimal-2',
  'decimal-e2',
  'decimal-4'
]

export function isComponentSourceCents (sourceType) {
  return _.includes(SOURCE_CENT_COMPONENT_TYPES, sourceType)
}

export default class ReportElement extends Model {
  static name = 'ReportElement'

  static versioned = true

  static atlasable = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.OperationsReporting]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      componentName: {
        type: String
      },
      defaultOptions: {
        type: JsonObject,
        default () {
          return {}
        }
      },
      overrideOptions: {
        type: JsonObject,
        default () {
          return {}
        }
      },
      finalOptions: {
        type: JsonObject,
        default () {
          return {}
        },
        output: true
      },
      reportId: {
        type: ID
      },

      type: {
        type: String,
        output: true
      },
      classes: {
        type: String,
        output: true
      },
      title: {
        type: String,
        output: true
      },
      hidden: {
        type: Boolean,
        output: true
      },

      reportStatus: {
        type: ReportStatus,
        output: true
      },

      chart: {
        type: ReportChart,
        output: true
      },
      table: {
        type: ReportTable,
        output: true
      },
      view: {
        type: ReportView,
        output: true
      },
      widget: {
        type: Widget,
        output: true
      },
      html: {
        type: ReportHtml,
        output: true
      },
      action: {
        type: String,
        input: true
      },
      atlas: {
        type: Boolean,
        input: true,
        default: false
      },
      controls: {
        type: [String],
        output: true,
        excludeFromDefault: true
      },
      possibleOptionsValues: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      rawData: {
        type: JsonArray,
        default () {
          return []
        },
        output: true
      },

      reportOptions: {
        type: JsonObject,
        default () {
          return {}
        },
        local: true
      }
    }

    this.computed = {
      filterable () {
        return _.some(this.columns, 'filterable')
      },
      groupable () {
        return _.some(this.columns, 'groupable')
      }
    }

    this.paginate = true
    this.orderable = true
  }

  optionsForSelect (optionType) {
    return _.map(this.possibleOptionsValues[optionType], value => {
      if (value.name || value.label) {
        return {
          value: value.value || value.id,
          label: value.label || value.name
        }
      } else {
        return { value, label: _.startCase(value) }
      }
    })
  }

  groupbyOptionsWithSeparators (groupBySearchValue) {
    const options = _.map(this.possibleOptionsValues.group_by, value => {
      const isNamedValue = value.name || value.label
      const columnOption = _.find(this.columns, {
        field: isNamedValue ? (value.value || value.id) : value
      })
      const optionValue = isNamedValue ? (value.value || value.id) : value
      return {
        value: optionValue,
        label: isNamedValue ? (value.label || value.name) : _.startCase(value),
        category: columnOption?.category || 'Other',
        definition: columnOption?.definition,
        key: `option-${optionValue}`
      }
    })

    return getOptionsWithSeparators(options, [], groupBySearchValue)
  }

  get migratable () {
    const sanitizedComponentName = this.componentName.replace(/\s+/g, '')
    return MIGRATABLE_COMPONENT_NAMES.includes(sanitizedComponentName)
  }

  get columns () {
    return this.table?.columns?.data || this.chart?.columns?.data || this.widget?.columns?.data
  }

  get filterableColumns () {
    return _.filter(this.columns, 'filterable')
  }

  columnNamesForSelect (columnSearchValue) {
    const columns = this.table?.columns?.data || []
    if (!columns.length) {
      return []
    }
    const filteredColumns = columns.filter(column => !column.groupable)
    const options = filteredColumns.map(column => ({
      value: column.field,
      label: column.label || _.startCase(column.field),
      category: column.category || 'Other',
      key: `option-${column.field}`,
      definition: column.definition
    }))

    return getOptionsWithSeparators(options, [], columnSearchValue)
  }

  isFieldFiltered (field) {
    return !!(this.finalOptions && this.finalOptions[`${field}_filter`] && this.finalOptions[`${field}_filter`].length)
  }

  isFieldExcludeFiltered (field) {
    return this.isFieldFiltered && this.reportOptions[`exclude_${field}`]
  }

  resetFieldFilter (field) {
    this.reportOptions[`${field}_filter`] = []
  }

  filterToFieldValue (field, value) {
    value = value?.value || value
    if (value === false) {
      value = 'false'
    } else if (value === true) {
      value = 'true'
    }
    this.reportOptions[`exclude_${field}`] = false
    this.reportOptions[`${field}_filter`] = [value]
  }

  excludeFieldValue (field, value) {
    value = value?.value || value
    if (value === false) {
      value = 'false'
    } else if (value === true) {
      value = 'true'
    }
    if (!this.reportOptions[`exclude_${field}`]) {
      this.reportOptions[`exclude_${field}`] = true
      this.reportOptions[`${field}_filter`] = [value]
    } else if (!_.includes(this.reportOptions[`${field}_filter`], value)) {
      this.reportOptions[`${field}_filter`] ||= []
      this.reportOptions[`${field}_filter`].push(value)
    }
  }

  resetAllFilters (reportOptions = null) {
    reportOptions ||= this.reportOptions
    _.each(this.filterableColumns, column => {
      const field = column.field
      reportOptions[`exclude_${field}`] = false
      reportOptions[`${field}_filter`] = []
    })
  }

  matchFilters (sourceReportOptions, destinationReportOptions = null) {
    destinationReportOptions ||= this.reportOptions
    _.each(this.filterableColumns, column => {
      const field = column.field
      destinationReportOptions[`exclude_${field}`] = sourceReportOptions[`exclude_${field}`]
      const sourceFilters = sourceReportOptions[`${field}_filter`]
      destinationReportOptions[`${field}_filter`] = sourceFilters ? [...sourceFilters] : null
    })
  }

  async migrateElement () {
    this.action = ELEMENT_ACTIONS.MIGRATE_ELEMENT

    let response
    try {
      response = await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }

    if (response && response.errors && response.errors.length) {
      console.log('Error migrating report element')
      console.log(response)
    }

    return response
  }

  async publishOptionsChange (atlasReport = false) {
    if (!this.reportOptions) return
    // Save a copy so the results returned do not overwrite the current mounted element
    const reportElement = new ReportElement({
      id: this.id,
      defaultOptions: this.reportOptions,
      atlas: atlasReport
    }, {
      fields: ['id', 'defaultOptions', 'atlas']
    })
    reportElement.reportOptions = null

    await reportElement.save()
    reportElement.destroy()
  }

  discardOptionsChange () {
    if (!this.reportOptions) return

    this.reportOptions = this.defaultOptions
  }

  saveOptionChange (options) {
    this.defaultOptions = {
      ...this.defaultOptions,
      ...options
    }
    this.save()
  }
}
